import { queriesAccounts } from "@query";
import { sessionService } from "@services/sessionService";
import { tokenStore } from "@services/tokenStore";
import { useSession } from "@hooks/useSession";
import {
  WSCard,
  WSControl,
  WSControlGroupCardOption,
  WSControlGroupCards,
  WSForm
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { BrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { useWSMutation } from "../../../../query/helpers";
import { WSQueries } from "../../../../query/WSQuery";
import { Layout } from "../../../../shared/components/Layout";
import { selectorAccountDisplayName } from "../../../../shared/selectors/selectorAccountDisplayName";

export interface SelectAccountProps {
  onClose: (result: boolean) => void;
}

type FormData = {
  accountId: string;
  remember: boolean;
};

// Custom (non-SDK) mutation: rebinds the v3 session to the chosen Account and,
// when requested, persists it as the Person's preferred account.
const useMutationSwitchAccount = () =>
  useWSMutation<void, unknown, FormData>(data =>
    sessionService.switchAccount(data.accountId, { preferred: data.remember })
  );

export const ModalSelectAccounts: React.FC<SelectAccountProps> = ({
  onClose
}) => {
  // The preferred account is stored per Person.
  const { personId } = useSession();

  const preferredAccountId = tokenStore.getPreferredAccountId(personId);

  const [switchAccount, switchAccountMeta] = useMutationSwitchAccount();

  const queryAccounts = queriesAccounts.listAccounts.useQuery();

  return (
    <Layout title="Select account">
      <BrowserPageTitle title="Select Account" />

      <WSCard
        header={{
          label: {
            text: "Who would you like to continue as?",
            helperText: "You have access to the following accounts"
          }
        }}
      >
        <WSQueries queries={{ queryAccounts }}>
          {({ queryAccountsData }) => {
            const accounts = queryAccountsData.data;

            const handleSubmit = async (data: FormData) => {
              await switchAccount(data, {
                onSuccess: () => {
                  onClose(true);
                }
              });
            };

            return (
              <WSForm<FormData>
                defaultValues={{
                  accountId: preferredAccountId ?? "",
                  remember: true
                }}
                validationSchema={Yup.object().shape({
                  accountId: Yup.string().required("Please select an account")
                })}
                onSubmit={handleSubmit}
              >
                <WSForm.Field
                  name="accountId"
                  component={WSControlGroupCards}
                  componentProps={{
                    options: accounts.map(
                      (account): WSControlGroupCardOption => {
                        const name = selectorAccountDisplayName(account);

                        return {
                          value: account.id!,
                          header: {
                            label: {
                              avatar: {
                                type: "text",
                                text: name.charAt(0)
                              },
                              text: name
                            }
                          }
                        };
                      }
                    )
                  }}
                />

                <WSForm.Field
                  my="2XL"
                  name="remember"
                  component={WSControl}
                  componentProps={{
                    type: "switch",
                    label: "Always log in to this account"
                  }}
                />

                <ErrorAlert
                  mb="2XL"
                  error={switchAccountMeta.error}
                  context="AccountSwitch"
                />

                <WSForm.SubmitButton name="continue" fullWidth>
                  Continue
                </WSForm.SubmitButton>
              </WSForm>
            );
          }}
        </WSQueries>
      </WSCard>
    </Layout>
  );
};
